import React, { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { ColorChip } from "../ui";
import { ColorPicker, Check, useCrud } from "./common";
import { T, TYPE, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const th = { "& th": { ...labelSx, bgcolor: T.surfaceHeader } };

/** Generic table + dialog editor for simple config rows. */
function RowEditor({ path, columns, fields, empty, title, sortRows = (r) => r, params, extra }) {
  const { rows, create, update, remove } = useCrud(path, params);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  if (!rows) return <CircularProgress size={22} />;
  const save = async () => {
    const { _id, ...body } = edit;
    const ok = _id ? await update(_id, body) : await create(body);
    if (ok) setEdit(null);
  };
  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit({ ...empty })}>Add {title}</Button>
      </Stack>
      <Table size="small">
        <TableHead><TableRow sx={th}>{columns.map(([, l]) => <TableCell key={l}>{l}</TableCell>)}<TableCell /></TableRow></TableHead>
        <TableBody>
          {sortRows(rows).map((r) => (
            <TableRow key={r._id} hover>
              {columns.map(([k, l, fmt]) => <TableCell key={l} sx={{ fontSize: TYPE.small }}>{fmt ? fmt(r[k], r) : String(r[k] ?? "—")}</TableCell>)}
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                <IconButton size="small" onClick={() => setEdit({ ...r })}><Pencil size={15} /></IconButton>
                <IconButton size="small" onClick={() => setConfirm(r)}><Trash2 size={15} color="#B42318" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && <TableRow><TableCell colSpan={columns.length + 1} sx={{ color: T.inkMuted }}>Nothing configured yet</TableCell></TableRow>}
        </TableBody>
      </Table>
      {extra}
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{edit._id ? `Edit ${title}` : `New ${title}`}</DialogTitle>
          <DialogContent dividers>{fields(edit, (patch) => setEdit((p) => ({ ...p, ...patch })))}</DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button></DialogActions>
        </Dialog>
      )}
      {confirm && (
        <Dialog open onClose={() => setConfirm(null)}>
          <DialogTitle>Delete "{confirm.name || confirm.title || confirm.label}"?</DialogTitle>
          <DialogContent><Typography sx={{ fontSize: TYPE.small }}>Items in use cannot be deleted — deactivate them instead.</Typography></DialogContent>
          <DialogActions><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={async () => { await remove(confirm._id); setConfirm(null); }}>Delete</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

const STATUS_TYPES = [["new", "New"], ["open", "Open / in progress"], ["pending", "Pending (waiting on customer)"], ["on_hold", "On hold"], ["resolved", "Resolved"], ["closed", "Closed"]];

export const StatusesEditor = () => (
  <RowEditor
    path="/statuses" title="status"
    empty={{ name: "", type: "open", color: "#7A5AF8", order: 10, pausesSla: false, isDefault: false, isReopen: false, isActive: true }}
    sortRows={(r) => [...r].sort((a, b) => a.order - b.order)}
    columns={[
      ["name", "Status", (v, r) => <ColorChip label={v} color={r.color} />], ["type", "Type"], ["order", "Order"],
      ["pausesSla", "Pauses SLA", (v) => (v ? "Yes" : "")], ["isDefault", "Default", (v) => (v ? "New tickets" : "")],
      ["isReopen", "Reopen to", (v) => (v ? "Yes" : "")], ["isActive", "Active", (v) => (v ? "Yes" : "No")],
    ]}
    fields={(e, set) => (
      <Grid container spacing={2}>
        <Grid item xs={8}><TextField fullWidth label="Name" value={e.name} onChange={(x) => set({ name: x.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth type="number" label="Order" value={e.order} onChange={(x) => set({ order: Number(x.target.value) })} /></Grid>
        <Grid item xs={12}><TextField select fullWidth label="Behaves as" value={e.type} onChange={(x) => set({ type: x.target.value })} helperText="Drives SLA, reopen and reporting behaviour">{STATUS_TYPES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}</TextField></Grid>
        <Grid item xs={12}><ColorPicker value={e.color} onChange={(color) => set({ color })} /></Grid>
        <Grid item xs={12}>
          <Check label="Pauses the SLA clock" checked={e.pausesSla} onChange={(pausesSla) => set({ pausesSla })} />
          <Check label="Default for new tickets" checked={e.isDefault} onChange={(isDefault) => set({ isDefault })} />
          <Check label="Status used when a ticket reopens" checked={e.isReopen} onChange={(isReopen) => set({ isReopen })} />
          <Check label="Active" checked={e.isActive} onChange={(isActive) => set({ isActive })} />
        </Grid>
      </Grid>
    )}
  />
);

export const PrioritiesEditor = () => {
  const ctx = useHd();
  return (
    <RowEditor
      path="/priorities" title="priority"
      empty={{ name: "", level: 5, color: "#F79009", order: 5, isDefault: false, isActive: true }}
      sortRows={(r) => [...r].sort((a, b) => a.level - b.level)}
      columns={[["name", "Priority", (v, r) => <ColorChip label={v} color={r.color} />], ["level", "Level"], ["isDefault", "Default", (v) => (v ? "Yes" : "")], ["isActive", "Active", (v) => (v ? "Yes" : "No")]]}
      extra={<Alert severity="info" sx={{ mt: 2 }}>SLA targets and escalation for each priority are set in <b>SLA policies</b>. A new priority has no SLA until you add a target for it ({ctx.priorities.length} priorities configured).</Alert>}
      fields={(e, set) => (
        <Grid container spacing={2}>
          <Grid item xs={8}><TextField fullWidth label="Name" value={e.name} onChange={(x) => set({ name: x.target.value })} /></Grid>
          <Grid item xs={4}><TextField fullWidth type="number" label="Level (higher = more urgent)" value={e.level} onChange={(x) => set({ level: Number(x.target.value) })} /></Grid>
          <Grid item xs={12}><ColorPicker value={e.color} onChange={(color) => set({ color })} /></Grid>
          <Grid item xs={12}><Check label="Default priority" checked={e.isDefault} onChange={(isDefault) => set({ isDefault })} /><Check label="Active" checked={e.isActive} onChange={(isActive) => set({ isActive })} /></Grid>
        </Grid>
      )}
    />
  );
};

export const CategoriesEditor = () => {
  const ctx = useHd();
  const { rows, create, update, remove } = useCrud("/categories");
  const [edit, setEdit] = useState(null);
  if (!rows) return <CircularProgress size={22} />;
  const children = (id) => rows.filter((r) => String(r.parentId || "") === String(id || "")).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  const LEVEL_CHILD = { category: "subcategory", subcategory: "issue type" };
  const Node = ({ c, depth }) => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: depth * 3, py: 0.5, borderBottom: `1px solid ${T.border}` }}>
        {depth > 0 && <ChevronRight size={14} color={T.inkMuted} />}
        <Typography sx={{ fontSize: TYPE.small, fontWeight: depth === 0 ? 700 : 500, opacity: c.isActive ? 1 : 0.5, flexGrow: 1 }}>{c.name}</Typography>
        {c.defaultTeamId && <Chip size="small" label={`Team: ${ctx.teamById.get(String(c.defaultTeamId))?.name || "?"}`} />}
        {c.defaultPriorityId && <Chip size="small" label={`Priority: ${ctx.priorityById.get(String(c.defaultPriorityId))?.name || "?"}`} />}
        {LEVEL_CHILD[c.level] && <Button size="small" onClick={() => setEdit({ name: "", parentId: c._id, isActive: true, order: 0 })}>+ {LEVEL_CHILD[c.level]}</Button>}
        <IconButton size="small" onClick={() => setEdit({ ...c })}><Pencil size={14} /></IconButton>
        <IconButton size="small" onClick={() => remove(c._id)}><Trash2 size={14} color="#B42318" /></IconButton>
      </Stack>
      {children(c._id).map((x) => <Node key={x._id} c={x} depth={depth + 1} />)}
    </Box>
  );
  const save = async () => {
    const { _id, level, clientId, createdAt, updatedAt, __v, ...body } = edit;
    if (_id) delete body.parentId;
    const ok = _id ? await update(_id, body) : await create(body);
    if (ok) setEdit(null);
  };
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>Category → Subcategory → Issue type. Defaults route new tickets automatically.</Typography>
        <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit({ name: "", parentId: null, isActive: true, order: 0 })}>Add category</Button>
      </Stack>
      {children(null).map((c) => <Node key={c._id} c={c} depth={0} />)}
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{edit._id ? "Edit" : "New"} {edit.parentId ? (rows.find((r) => String(r._id) === String(edit.parentId))?.level === "category" ? "subcategory" : "issue type") : "category"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={8}><TextField fullWidth label="Name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField fullWidth type="number" label="Order" value={edit.order} onChange={(e) => setEdit((p) => ({ ...p, order: Number(e.target.value) }))} /></Grid>
              <Grid item xs={6}><TextField select fullWidth label="Default team" value={edit.defaultTeamId || ""} onChange={(e) => setEdit((p) => ({ ...p, defaultTeamId: e.target.value || null }))}><MenuItem value="">None</MenuItem>{ctx.teams.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={6}><TextField select fullWidth label="Default priority" value={edit.defaultPriorityId || ""} onChange={(e) => setEdit((p) => ({ ...p, defaultPriorityId: e.target.value || null }))}><MenuItem value="">None</MenuItem>{ctx.priorities.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12}><Check label="Active" checked={edit.isActive} onChange={(isActive) => setEdit((p) => ({ ...p, isActive }))} /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

const FIELD_TYPES = ["text", "textarea", "number", "email", "phone", "date", "datetime", "dropdown", "multiselect", "checkbox", "radio", "url"];
export const CustomFieldsEditor = () => (
  <RowEditor
    path="/custom-fields" title="field"
    empty={{ entity: "ticket", key: "", label: "", type: "text", options: [], required: false, visibleToCustomer: false, order: 0, isActive: true }}
    columns={[["label", "Label"], ["key", "Key"], ["entity", "Entity"], ["type", "Type"], ["required", "Required", (v) => (v ? "Yes" : "")], ["isActive", "Active", (v) => (v ? "Yes" : "No")]]}
    fields={(e, set) => (
      <Grid container spacing={2}>
        <Grid item xs={6}><TextField fullWidth label="Label" value={e.label} onChange={(x) => set({ label: x.target.value, ...(!e._id && !e.keyTouched ? { key: x.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) } : {}) })} /></Grid>
        <Grid item xs={6}><TextField fullWidth label="Key" value={e.key} disabled={Boolean(e._id)} onChange={(x) => set({ key: x.target.value, keyTouched: true })} helperText="lower_snake_case, cannot change later" /></Grid>
        <Grid item xs={6}><TextField select fullWidth label="Applies to" value={e.entity} disabled={Boolean(e._id)} onChange={(x) => set({ entity: x.target.value })}>{["ticket", "contact", "conversation"].map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}</TextField></Grid>
        <Grid item xs={6}><TextField select fullWidth label="Type" value={e.type} disabled={Boolean(e._id)} onChange={(x) => set({ type: x.target.value })}>{FIELD_TYPES.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}</TextField></Grid>
        {["dropdown", "multiselect", "radio"].includes(e.type) && (
          <Grid item xs={12}><TextField fullWidth label="Options (one per line)" multiline minRows={3} value={(e.options || []).join("\n")} onChange={(x) => set({ options: x.target.value.split("\n") })} /></Grid>
        )}
        <Grid item xs={12}><Check label="Required" checked={e.required} onChange={(required) => set({ required })} /><Check label="Active" checked={e.isActive} onChange={(isActive) => set({ isActive })} /></Grid>
      </Grid>
    )}
  />
);

const MACRO_TYPES = [["set_status", "Set status"], ["set_priority", "Set priority"], ["assign_agent", "Assign to agent"], ["assign_team", "Assign to team"], ["add_tags", "Add tags"]];
const MACRO_DEFAULTS = { set_status: { statusId: "" }, set_priority: { priorityId: "" }, assign_agent: { assigneeId: "me" }, assign_team: { teamId: "" }, add_tags: { tags: [] } };

/** Actions a canned response also applies to the ticket once the reply is sent. */
function MacroActionsEditor({ value = [], onChange }) {
  const ctx = useHd();
  const setAt = (i, patch) => onChange(value.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const param = (a, i) => {
    const setP = (params) => setAt(i, { params: { ...a.params, ...params } });
    const sel = (key, options) => (
      <TextField select fullWidth size="small" label="Value" value={a.params?.[key] || ""} onChange={(x) => setP({ [key]: x.target.value })}>
        {options.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
      </TextField>
    );
    if (a.type === "set_status") return sel("statusId", ctx.statuses.map((x) => [x._id, x.name]));
    if (a.type === "set_priority") return sel("priorityId", ctx.priorities.map((x) => [x._id, x.name]));
    if (a.type === "assign_agent") return sel("assigneeId", [["me", "The agent using the macro"], ...ctx.agents.map((x) => [x.memberId, x.name])]);
    if (a.type === "assign_team") return sel("teamId", ctx.teams.map((x) => [x._id, x.name]));
    return <TextField fullWidth size="small" label="Tags (comma separated)" value={(a.params?.tags || []).join(", ")} onChange={(x) => setP({ tags: x.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} />;
  };
  return (
    <Box>
      <Typography sx={{ ...labelSx, mb: 0.5 }}>Macro actions (optional)</Typography>
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mb: 1 }}>Run after the agent sends the reply, with the agent's own permissions.</Typography>
      <Stack spacing={1}>
        {value.map((a, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <TextField select size="small" label="Action" value={a.type} sx={{ minWidth: 180 }} onChange={(x) => setAt(i, { type: x.target.value, params: { ...MACRO_DEFAULTS[x.target.value] } })}>
              {MACRO_TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            <Box sx={{ flexGrow: 1 }}>{param(a, i)}</Box>
            <IconButton size="small" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 size={15} color="#B42318" /></IconButton>
          </Stack>
        ))}
        <Box>
          <Button size="small" startIcon={<Plus size={14} />} disabled={value.length >= 10} onClick={() => onChange([...value, { type: "set_status", params: { ...MACRO_DEFAULTS.set_status } }])}>Add action</Button>
        </Box>
      </Stack>
    </Box>
  );
}

export const CannedEditor = () => (
  <RowEditor
    path="/canned-responses" title="canned response"
    empty={{ title: "", shortcut: "", body: "", shared: true, actions: [] }}
    columns={[["title", "Title"], ["shortcut", "Shortcut"], ["actions", "Macro", (v) => (v?.length ? `${v.length} action${v.length === 1 ? "" : "s"}` : "")], ["shared", "Shared", (v) => (v ? "Team" : "Only me")]]}
    fields={(e, set) => (
      <Grid container spacing={2}>
        <Grid item xs={8}><TextField fullWidth label="Title" value={e.title} onChange={(x) => set({ title: x.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth label="Shortcut" value={e.shortcut} onChange={(x) => set({ shortcut: x.target.value })} /></Grid>
        <Grid item xs={12}><TextField fullWidth multiline minRows={5} label="Reply" value={e.body} onChange={(x) => set({ body: x.target.value })} helperText="You can use {{customer_name}}, {{ticket_id}}, {{agent_name}}" /></Grid>
        <Grid item xs={12}><MacroActionsEditor value={e.actions || []} onChange={(actions) => set({ actions })} /></Grid>
        <Grid item xs={12}><Check label="Share with the whole team" checked={e.shared} onChange={(shared) => set({ shared })} /></Grid>
      </Grid>
    )}
  />
);
